import React, { useState, useEffect } from "react";
import { authService } from "../services/auth.service";

interface Admin {
  id: number;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export default function AdminManagement() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  const validateName = (name: string) => {
    return name.trim().length >= 2 && name.length <= 100 && /^[a-zA-Z\sÀ-ſ]+$/.test(name);
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await authService.makeAuthenticatedRequest('/admin/list');
      if (response.ok) {
        const data = await response.json();
        setAdmins(Array.isArray(data) ? data : []);
      } else {
        setError("Erro ao carregar lista de administradores");
      }
    } catch (err: any) {
      setError(err.message || "Erro ao carregar administradores");
      console.error("Erro ao carregar admins:", err);
    } finally {
      setLoading(false);
    }
  };

  const openModalToAdd = () => {
    setEditingAdmin(null);
    setName("");
    setEmail("");
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const openModalToEdit = (admin: Admin) => {
    setEditingAdmin(admin);
    setName(admin.name);
    setEmail(admin.email);
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
      e.preventDefault();

      if (!name.trim() || !email.trim()) {
        setError("Preencha todos os campos.");
        return;
      }

      if (!validateName(name.trim())) {
        setError("Nome deve ter entre 2-100 caracteres e conter apenas letras.");
        return;
      }

      if (!validateEmail(email.trim())) {
        setError("Por favor, insira um e-mail válido.");
        return;
      }

      setSaveLoading(true);
      setError("");

      try {
        const sanitizedName = name.trim();
        const sanitizedEmail = email.trim().toLowerCase();

        if (editingAdmin) {
          const response = await authService.makeAuthenticatedRequest(`/admin/${editingAdmin.id}`, {
            method: 'PUT',
          body: JSON.stringify({ name: sanitizedName, email: sanitizedEmail })
          });

          if (response.ok) {
            await loadAdmins();
            setSuccess("Administrador atualizado com sucesso!");
            setName("");
            setEmail("");
            setEditingAdmin(null);
            setShowModal(false);
            setTimeout(() => setSuccess(""), 5000);
          } else {
            const errorData = await response.json();
            setError(errorData.message || "Erro ao atualizar administrador");
          }
        } else {
          const response = await authService.makeAuthenticatedRequest('/admin/invite', {
            method: 'POST',
            body: JSON.stringify({ name: sanitizedName, email: sanitizedEmail }),
          });

          if (response.ok) {
            await loadAdmins();
            setSuccess(`Convite enviado com sucesso para ${sanitizedEmail}! O novo administrador receberá um email com instruções para criar a senha.`);
            setName("");
            setEmail("");
            setEditingAdmin(null);
            setShowModal(false);
            setTimeout(() => setSuccess(""), 8000);
          } else {
            const errorData = await response.json();
            setError(errorData.message || "Erro ao convidar novo administrador");
          }
        }
      } catch (err: any) {
        setError(err.message || "Erro ao salvar administrador");
        console.error("Erro ao salvar admin:", err);
      } finally {
        setSaveLoading(false);
      }
  };


  const handleRemove = async (admin: Admin) => {
    const currentAdmin = authService.getCurrentAdmin();

    if (currentAdmin && String(currentAdmin.id) === String(admin.id)) {
      setError("Você não pode remover a si mesmo.");
      setTimeout(() => setError(""), 5000);
      return;
    }

    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;

    setDeleteLoading(true);
    setError("");

    try {
      const response = await authService.makeAuthenticatedRequest(`/admin/${adminToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadAdmins();
        setSuccess(`Administrador ${adminToDelete.name} removido com sucesso!`);
        setTimeout(() => setSuccess(""), 5000);
        setShowDeleteModal(false);
        setAdminToDelete(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Erro ao remover administrador");
        setTimeout(() => setError(""), 5000);
      }
    } catch (err: any) {
      setError(err.message || "Erro ao remover administrador");
      setTimeout(() => setError(""), 5000);
      console.error("Erro ao remover admin:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: 32,
        background: "var(--background)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1000,
          background: "var(--card)",
          padding: 32,
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "var(--card-foreground)" }}>
            Gerenciamento de Administradores
          </h1>
          <button
            onClick={openModalToAdd}
            style={{
              background: "var(--accent)",
              color: "var(--accent-foreground)",
              fontWeight: 600,
              padding: "12px 20px",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              transition: "0.2s",
            }}
          >
            + Novo Administrador
          </button>
        </div>

        {error && (
          <div
            style={{
              background: "var(--destructive)",
              color: "white",
              padding: 12,
              borderRadius: 8,
              marginBottom: 24,
              textAlign: "center",
            }}
          >
            {error}
            <button
              onClick={() => setError("")}
              style={{
                background: "transparent",
                border: "none",
                color: "white",
                marginLeft: 8,
                cursor: "pointer",
                fontSize: 16,
              }}
            >
              ×
            </button>
          </div>
        )}

        {success && (
          <div
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
              padding: 16,
              borderRadius: 8,
              marginBottom: 24,
              textAlign: "center",
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
            }}
          >
            {success}
            <button
              onClick={() => setSuccess("")}
              style={{
                background: "transparent",
                border: "none",
                color: "white",
                marginLeft: 8,
                cursor: "pointer",
                fontSize: 16,
              }}
            >
              ×
            </button>
          </div>
        )}

        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              color: "var(--muted-foreground)",
            }}
          >
            Carregando lista de administradores...
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  background: "var(--muted)",
                  color: "var(--muted-foreground)",
                  textAlign: "left",
                }}
              >
                <th style={{ padding: "14px 12px", fontWeight: 600 }}>Nome</th>
                <th style={{ padding: "14px 12px", fontWeight: 600 }}>E-mail</th>
                <th style={{ padding: "14px 12px", fontWeight: 600 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      textAlign: "center",
                      padding: 40,
                      color: "var(--muted-foreground)",
                    }}
                  >
                    Nenhum administrador encontrado
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr
                    key={admin.id}
                    style={{
                      borderBottom: "1px solid var(--border)",
                      transition: "0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--muted)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <td style={{ padding: "14px 12px" }}>{admin.name}</td>
                    <td style={{ padding: "14px 12px" }}>{admin.email}</td>
                    <td style={{ padding: "14px 12px", display: "flex", gap: 8 }}>
                      <button
                        onClick={() => openModalToEdit(admin)}
                        style={{
                          background: "var(--accent)",
                          color: "var(--accent-foreground)",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleRemove(admin)}
                        style={{
                          background: "var(--destructive)",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--card)",
              padding: 32,
              borderRadius: 16,
              width: "100%",
              maxWidth: 500,
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            }}
          >
            <h2
              style={{
                fontSize: 24,
                fontWeight: 700,
                marginBottom: 20,
                color: "var(--card-foreground)",
              }}
            >
              {editingAdmin ? "Editar Administrador" : "Novo Administrador"}
            </h2>
            <form
              onSubmit={handleSave}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              <input
                type="text"
                placeholder="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  background: "var(--input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 16,
                }}
              />
              <input
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  background: "var(--input)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 16,
                }}
              />
              {/* {!editingAdmin && (
                <input
                  type="password"
                  placeholder="Senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    background: "var(--input)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 16,
                  }}
                />
              )} */}
              {error && <div style={{ color: "var(--destructive)" }}>{error}</div>}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "var(--muted)",
                    color: "var(--card-foreground)",
                    padding: "10px 16px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saveLoading}
                  style={{
                    background: saveLoading ? "var(--muted)" : "var(--accent)",
                    color: saveLoading ? "var(--muted-foreground)" : "var(--accent-foreground)",
                    fontWeight: 600,
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: 8,
                    cursor: saveLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {saveLoading
                    ? "Salvando..."
                    : editingAdmin
                      ? "Salvar alterações"
                      : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1001,
            animation: "fadeIn 0.3s ease-out",
          }}
          onClick={cancelDelete}
        >
          <div
            style={{
              background: "var(--card)",
              padding: 40,
              borderRadius: 20,
              maxWidth: 480,
              width: "90%",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5), 0 10px 20px rgba(0, 0, 0, 0.3)",
              animation: "modalBounceIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
              border: "1px solid var(--border)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #fee2e2 0%, #ef4444 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  boxShadow: "0 8px 16px rgba(239, 68, 68, 0.3)",
                }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#7f1d1d" strokeWidth="2.5">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
                </svg>
              </div>
              <h3
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  marginBottom: 12,
                  color: "var(--card-foreground)",
                  letterSpacing: "-0.5px",
                }}
              >
                Confirmar Exclusão
              </h3>
              <p
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: 15,
                  lineHeight: 1.6,
                  marginBottom: 8,
                }}
              >
                Tem certeza que deseja remover o administrador
              </p>
              <p
                style={{
                  color: "var(--card-foreground)",
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                {adminToDelete?.name}?
              </p>
              <p
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: 14,
                  marginTop: 8,
                }}
              >
                ({adminToDelete?.email})
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "stretch" }}>
              <button
                onClick={cancelDelete}
                disabled={deleteLoading}
                style={{
                  flex: 1,
                  background: "var(--muted)",
                  color: "var(--card-foreground)",
                  border: "2px solid var(--border)",
                  padding: "14px 24px",
                  borderRadius: 12,
                  cursor: deleteLoading ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  opacity: deleteLoading ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.background = "var(--card)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.background = "var(--muted)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                style={{
                  flex: 1,
                  background: deleteLoading ? "var(--muted)" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  padding: "14px 24px",
                  borderRadius: 12,
                  cursor: deleteLoading ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: 15,
                  boxShadow: deleteLoading ? "none" : "0 4px 14px rgba(239, 68, 68, 0.4)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                onMouseEnter={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(239, 68, 68, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deleteLoading) {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(239, 68, 68, 0.4)";
                  }
                }}
              >
                {deleteLoading ? "Removendo..." : "Remover"}
              </button>
            </div>
          </div>

          <style>
            {`
              @keyframes fadeIn {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }

              @keyframes modalBounceIn {
                0% {
                  opacity: 0;
                  transform: scale(0.7) translateY(-30px);
                }
                50% {
                  transform: scale(1.05);
                }
                100% {
                  opacity: 1;
                  transform: scale(1) translateY(0);
                }
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
}
